/**
 * M-D4b — one minimum per ordered worker PLACE and SHIFT.
 *
 * An order priced with `pricing.minimumRule.scope === 'place_shift'` bills its
 * hourly work in billing units (client, order, place, shift date). Who filled
 * the place does not matter: a replacement at noon is still one place and one
 * minimum. Legacy orders (no `minimumRule`) never come here — they keep the
 * (order, worker, shift) key in `clientTimeBilling.ts`, byte for byte.
 *
 * The arithmetic, in the order it runs (plan v3 B/C, v3.1):
 *
 *   1. Worker-shift: the sheets of one person's shift (`billableShiftGroups`,
 *      unchanged) — ONE commercial pause per worker-shift, identical to the
 *      legacy path, so payroll and client break minutes stay equal (I1).
 *      Billable work B_w = registered work − that pause's intervals. Every
 *      interval end, pauses included, is floored to the whole UTC minute
 *      (B1/B-2); money is counted in whole minutes from here on.
 *   2. α — each minute of B_w goes to the place whose segment of the SAME
 *      person on this order covers it; a minute before the first covered one
 *      goes to that first place, a later uncovered minute to the previous
 *      covered place; none covered → Home(w), the place on the worker-shift's
 *      earliest sheet; no Home → the minutes are unresolved (billing waits for
 *      `setTimesheetPlace`). A sheet whose place an admin set (`source:
 *      'admin'`) puts all its minutes on that place (C-3).
 *   3. Units: (place, shift date). Units sharing a worker-shift form a
 *      component. α: T_p = A_p > 0 ? max(0, M − A_p) : 0 per unit.
 *   4. β — the person's moved minutes go back to Home, but only at minutes
 *      when nobody else works on Home (registered work, recorded breaks
 *      excluded; C-1). β is used for the component only when it costs the
 *      client strictly less than α; a tie keeps α. A move inside a shift can
 *      therefore never add a minimum (Marti Q4).
 *   5. Money: `priceLaborLines` — minutes summed per (labor line, component,
 *      hour class, coefficient) over the document, cents = roundHalfUp(minutes
 *      × rate cents × coefficient bp / 600 000), rounded once (B4).
 *   6. M-D9 — only on an order with a `coefficientRule` that agrees a class:
 *      each billable minute gets ONE client class (public holiday > weekend
 *      > night among the classes agreed on the order, else regular; object
 *      zone and country), and a unit's top-up T is spread over the
 *      classes by the ordered schedule's unfilled time g′_k = max(0, S_k −
 *      In_k − P_k) (`allocateTopUp`). Without a rule every minute is
 *      'regular' — byte for byte the D4b result.
 *
 * The estimate and the proforma run the SAME functions on the order's plan
 * (`plannedBillingUnits`), so a plan worked exactly bills exactly the estimate.
 *
 * Pure. Functions import it by relative path; the browser through the package.
 */

import { billableShiftGroups, billableWorkRows, timesheetShiftKey } from './clientTimeBilling';
import { CLIENT_HOUR_CLASS_PRIORITY, clientHourClassifier } from './hourClasses';
import type { BillingHourClass, InvoiceBillingUnit, InvoiceLine } from './invoice';
import type {
  CoefficientRule,
  CoefficientRuleVersion,
  JobPricingSnapshot,
  JobShift,
  MinimumRule,
  MinimumRuleVersion,
} from './job';
import { needOf, segmentsOf, shiftOccurrences, type Segment, type ShiftOccurrence } from './staffingPlan';
import type { Timesheet, TimesheetShiftEnd } from './timesheet';
import { calendarDate, DEFAULT_WORK_TIME_ZONE } from './workCalendar';
import {
  registeredIntervals,
  subtractIntervals,
  workAfterUnpaidMinutes,
  type WorkInterval,
} from './workIntervals';

const MIN_MS = 60_000;

/** The parts of an order the unit arithmetic reads (its segments). */
export type PlaceShiftJob = Parameters<typeof segmentsOf>[0];

// ── The rule ─────────────────────────────────────────────────────────────────

/** True when the order bills one minimum per worker place and shift. */
export function isPlaceShiftPricing(pricing: Pick<JobPricingSnapshot, 'minimumRule'> | undefined | null): boolean {
  return pricing?.minimumRule?.scope === 'place_shift';
}

/**
 * The rule version that bills `shiftDate`: the current one from its
 * `fromShiftDate`, otherwise the newest `previous` version covering the date
 * (plan v2 F6 / v3 A).
 */
export function minimumRuleFor(rule: MinimumRule, shiftDate: string): MinimumRuleVersion {
  if (!rule.fromShiftDate || shiftDate >= rule.fromShiftDate) return rule;
  const older = [...(rule.previous ?? [])].reverse();
  return older.find((v) => !v.fromShiftDate || shiftDate >= v.fromShiftDate) ?? older[older.length - 1] ?? rule;
}

/** Minimum minutes for one unit on `shiftDate` (0 for B2C / piece / none). */
export function minimumMinutesFor(pricing: JobPricingSnapshot | undefined, shiftDate: string): number {
  if (!pricing || pricing.segment === 'b2c') return 0;
  const rule = pricing.minimumRule;
  const hours = rule ? minimumRuleFor(rule, shiftDate).hours : (pricing.minimumBillableHours ?? 0);
  return hours > 0 ? Math.round(hours * 60) : 0;
}

// ── M-D9: the coefficient rule ───────────────────────────────────────────────

/**
 * The coefficient rule version that bills `shiftDate`. A date before
 * `fromShiftDate` bills by the newest `previous` covering it; a date before
 * every version's `fromShiftDate` (or the order had no earlier rule) had
 * nothing agreed: × 1 — a rule applied from a date never reaches back.
 */
export function coefficientRuleFor(rule: CoefficientRule, shiftDate: string): CoefficientRuleVersion {
  if (!rule.fromShiftDate || shiftDate >= rule.fromShiftDate) return rule;
  const older = [...(rule.previous ?? [])].reverse();
  const hit = older.find((v) => !v.fromShiftDate || shiftDate >= v.fromShiftDate);
  if (hit) return hit;
  return {
    basis: 'none',
    holidayCountry: rule.holidayCountry,
    timeZone: rule.timeZone,
    topUp: rule.topUp,
    resolvedAt: rule.resolvedAt,
  };
}

const COEFFICIENT_FIELD = {
  night: 'night',
  weekend: 'weekend',
  public_holiday: 'publicHoliday',
} as const;

/** The agreed coefficient of a class in basis points; null = not agreed (bills as regular). */
export function agreedCoefficientBp(
  version: CoefficientRuleVersion | undefined,
  cls: BillingHourClass,
): number | null {
  if (!version || cls === 'regular') return null;
  const v = version[COEFFICIENT_FIELD[cls]];
  return typeof v === 'number' && v > 0 ? Math.round(v * 10_000) : null;
}

/** True when the version agrees at least one class coefficient. */
export function hasAgreedCoefficients(version: CoefficientRuleVersion | undefined): boolean {
  return (['night', 'weekend', 'public_holiday'] as const).some((c) => agreedCoefficientBp(version, c) != null);
}

/** Rank for "regular < night < weekend < public holiday" (S-1 tie order without a schedule). */
const CLASS_RANK: Record<BillingHourClass, number> = { regular: 0, night: 1, weekend: 2, public_holiday: 3 };

/**
 * The client class of a minute for one order and shift date, or null when the
 * order bills everything as 'regular' (no rule, or nothing agreed).
 *
 * Variant B (Marti 2026-11-03): the priority applies only among the classes
 * agreed on this version — Saturday 23:00 with only night agreed bills night.
 * A_k, S_k, In_k and P_k all use this one function, so the allocation, the
 * estimate, the proforma and the invoice agree on what a minute is.
 */
function classifierFor(
  pricing: JobPricingSnapshot | undefined,
  shiftDate: string,
  cache: Map<string, (ms: number) => BillingHourClass>,
): ((minute: number) => BillingHourClass) | null {
  const rule = pricing?.coefficientRule;
  if (!rule) return null;
  const version = coefficientRuleFor(rule, shiftDate);
  if (!hasAgreedCoefficients(version)) return null;
  const agreed = (['night', 'weekend', 'public_holiday'] as const).filter((c) => agreedCoefficientBp(version, c) != null);
  const key = `${version.timeZone}|${version.holidayCountry}|${agreed.join()}`;
  let classify = cache.get(key);
  if (!classify) {
    classify = clientHourClassifier(
      version.timeZone || DEFAULT_WORK_TIME_ZONE,
      version.holidayCountry || 'EE',
      new Set<BillingHourClass>(agreed),
    );
    cache.set(key, classify);
  }
  const c = classify;
  return (minute: number) => c(minute * MIN_MS);
}

// ── Results ──────────────────────────────────────────────────────────────────

export type TopUpBasis = 'place' | 'move_capped' | 'none';

export interface BillingUnitResult {
  /** JSON [clientId, jobId, 'place', placeId, shiftDate] — or 'unresolved' + the worker-shift key. */
  key: string;
  clientId?: string;
  jobId: string;
  /** null = the place could not be decided (billing waits for `setTimesheetPlace`). */
  placeId: string | null;
  shiftDate: string;
  offSchedule?: boolean;
  componentId: string;
  minimumMinutes: number;
  actualMinutes: number;
  topUpMinutes: number;
  /**
   * What bills now: max(A, M − P − D) for an active unit (v4.3 floor). When
   * the shiftEnd decision would change money (`pendingTopUpMinutes` > 0) the
   * component does not bill at all until it is decided.
   */
  billedMinutes: number;
  actualMinutesByClass: Partial<Record<BillingHourClass, number>>;
  topUpMinutesByClass: Partial<Record<BillingHourClass, number>>;
  topUpBasis: TopUpBasis;
  /** Sheets and their minutes in this unit, chronological. Σ minutes = actualMinutes. */
  members: { sheetId: string; minutes: number }[];
  /** Workers who filled the place in this unit, in order of first minute. */
  workerIds: string[];
  /** Hours for displays (2 dp); the money uses the minutes. */
  hours: number;
  billedHours: number;
  topUpHours: number;
  unresolved?: boolean;
  /** C-2 — an increment on a unit whose earlier days are already billed. */
  increment?: boolean;
  /**
   * C-2 — worked minutes of the late days already paid for by the minimum
   * billed earlier ("already covered by the billed minimum"). Not billed
   * again; `actualMinutes` holds only the part that is.
   */
  coveredByBilledMinimumMinutes?: number;
  /**
   * v4 — P: ordered minutes RM24 did not provide (no holder, holder not yet
   * arrived, released, or marked `provider_shortfall`). The minimum is
   * reduced by them, minute for minute (Marti C-1, lateness).
   */
  providerShortfallMinutes: number;
  /** Ordered minutes held for the client but not worked (pause in presence, client-ended tail). */
  heldMinutes: number;
  /** D: the tail after the holder's last checkout, segment continuing, no decision (v4.3). */
  undecidedMinutes: number;
  /** max(0, M − P). */
  effectiveMinimumMinutes: number;
  /** What a client_ended decision would add on top of `billedMinutes` (> 0 → billing gate). */
  pendingTopUpMinutes: number;
  /** P as merged ranges, for "RM24 did not fill 08:00–12:00". */
  shortfallRanges: { from: string; to: string }[];
  /** The undecided tail: from when, whose (the decision goes on their sheet). */
  undecided?: { from: string; workerId: string; sheetId: string };
  /** SHOULD-2 — a holder's arrival was judged off site (shown for a human to look at). */
  offSiteArrival?: boolean;
  /**
   * M-D9 — the unit's minutes were split into client classes (the order's
   * coefficient rule agrees one for this date). Absent = all 'regular'.
   */
  classified?: boolean;
  /** M-D9 — per member sheet, its minutes in this unit by class (classified units). */
  memberClasses?: Record<string, Partial<Record<BillingHourClass, number>>>;
  /** M-D9 — C-2: the classes of `coveredByBilledMinimumMinutes` (classified increments). */
  coveredMinutesByClass?: Partial<Record<BillingHourClass, number>>;
  /** M-D9 — the periods the top-up was spread over ("Kuidas arvutati"); classified units. */
  topUpPeriods?: TopUpPeriod[];
}

/** Per-sheet allocation for row displays (the minimum on the unit's last row). */
export interface BillingRow {
  hours: number;
  billedHours: number;
  breakMinutes: number;
  minutes: number;
  topUpMinutes: number;
  /**
   * M-D9 — billed hours weighted by the agreed coefficients (× rate = this
   * row's share of the money, unrounded). Only on an order with classified
   * units; the document's rows round once (B4), so a sum of these is ≤ 1 cent
   * per row off the invoice. Absent = `billedHours` is the whole story.
   */
  weightedBilledHours?: number;
}

export interface PlaceShiftBilling {
  units: BillingUnitResult[];
  /** `${jobId}/${sheetId}` → the sheet's share. */
  rows: Map<string, BillingRow>;
  /** Commercial pause per worker-shift (identical to the legacy path). */
  breakMinutesByShift: Map<string, number>;
  /** Per component: α and β (counterfactual) cost in cents, and which was used. */
  components: Map<string, { alphaCents: number; betaCents: number | null; usedBeta: boolean }>;
}

/** What the unit arithmetic needs besides the sheets. */
export interface PlaceShiftContext {
  pricing: JobPricingSnapshot | undefined;
  /**
   * The order's segments (`segmentsOf(job)`) — who held which place when
   * (α, P, held). Without them (plan, a view without the order) nothing is
   * known about shortfall: P = 0 and a sheet's minutes stay on its own place.
   */
  segments?: readonly Segment[];
  /** The order's shift occurrences (the ordered window S of each date). */
  occurrences?: readonly ShiftOccurrence[];
}

// ── Minutes ──────────────────────────────────────────────────────────────────

/** Whole UTC minutes of intervals, ends floored (B-2): [floor(start), floor(end)). */
function minutesOf(intervals: WorkInterval[]): number[] {
  const out = new Set<number>();
  for (const v of intervals) {
    const a = Math.floor(v.start / MIN_MS);
    const b = Math.floor(v.end / MIN_MS);
    for (let m = a; m < b; m += 1) out.add(m);
  }
  return [...out].sort((x, y) => x - y);
}

const minuteIso = (m: number) => new Date(m * MIN_MS).toISOString();

interface WorkerShift {
  key: string;
  workerId: string;
  members: Timesheet[];
  breakMinutes: number;
  /** Billable minutes (after the client pause), sorted. */
  billable: number[];
  /** minute → sheet id. */
  sheetAt: Map<number, string>;
  /** minute → α place (null = unresolved). */
  alpha: Map<number, string | null>;
  home: string | null;
  shiftDate: string;
  offSchedule: boolean;
  /** minute → pause minute (for per-row break display). */
  pauseMinutes: number[];
}

function homeOf(members: Timesheet[]): { home: string | null; shiftDate: string | null; offSchedule: boolean } {
  const first = members[0];
  const withPlace = members.find((ts) => ts.place);
  return {
    home: first?.place?.placeId ?? null,
    shiftDate: withPlace?.place?.shiftDate ?? null,
    offSchedule: !!withPlace?.place?.offSchedule,
  };
}

function alphaPlaces(
  ws: Pick<WorkerShift, 'workerId' | 'sheetAt' | 'home'>,
  minutes: number[],
  members: Timesheet[],
  segments: readonly Segment[] | undefined,
): Map<number, string | null> {
  const byId = new Map(members.map((ts) => [ts.id, ts]));
  const own = (segments ?? [])
    .filter((s) => s.workerId === ws.workerId && s.placeId)
    .map((s) => ({ start: Date.parse(s.startAt), end: Date.parse(s.endAt), placeId: s.placeId! }));
  const direct: (string | null)[] = minutes.map((m) => {
    const sheet = byId.get(ws.sheetAt.get(m) ?? '');
    if (sheet?.place?.source === 'admin') return sheet.place.placeId;
    if (!segments) return sheet?.place?.placeId ?? null;
    const at = m * MIN_MS;
    return own.find((s) => at >= s.start && at < s.end)?.placeId ?? null;
  });
  const firstCovered = direct.find((p) => p !== null) ?? null;
  const out = new Map<number, string | null>();
  let previous: string | null = null;
  minutes.forEach((m, i) => {
    const p = direct[i];
    if (p !== null) previous = p;
    out.set(m, p ?? previous ?? firstCovered ?? ws.home);
  });
  return out;
}

function workerShifts(sheets: Timesheet[], ctx: PlaceShiftContext): WorkerShift[] {
  const pricing = ctx.pricing;
  const b2c = pricing?.segment === 'b2c';
  const thresholdMinutes = (pricing?.clientTimeBilling?.thresholdHours ?? 6) * 60;
  return billableShiftGroups(sheets, pricing).map((group) => {
    const members = group.members;
    const entries = members.flatMap((ts) => ts.entries ?? []);
    const reg = registeredIntervals(entries);
    const pause = b2c
      ? []
      : (() => {
          const p = workAfterUnpaidMinutes(entries, group.breakMinutes, thresholdMinutes);
          return [...p.recorded, ...p.assumed];
        })();
    const gross = minutesOf(reg.work);
    const billableSet = new Set(minutesOf(subtractIntervals(reg.work, pause)));
    const pauseMinutes = gross.filter((m) => !billableSet.has(m));
    const sheetAt = new Map<number, string>();
    for (const ts of members)
      for (const m of minutesOf(registeredIntervals(ts.entries ?? []).work))
        if (!sheetAt.has(m)) sheetAt.set(m, ts.id);
    const { home, shiftDate, offSchedule } = homeOf(members);
    const ws: WorkerShift = {
      key: group.key,
      workerId: members[0]?.workerId ?? '',
      members,
      breakMinutes: b2c ? 0 : group.breakMinutes,
      billable: gross.filter((m) => billableSet.has(m)),
      sheetAt,
      alpha: new Map(),
      home,
      shiftDate:
        shiftDate ??
        calendarDate(
          members[0]?.entries?.[0]?.checkInAt ?? '',
          members[0]?.timeZone ?? DEFAULT_WORK_TIME_ZONE,
        ),
      offSchedule,
      pauseMinutes,
    };
    ws.alpha = alphaPlaces(ws, gross, members, ctx.segments);
    return ws;
  });
}

// ── Units ────────────────────────────────────────────────────────────────────

/**
 * S — the ordered window of a shift date, in whole UTC minutes: a sheet's own
 * plan snapshot first (on a scheduled sheet of that date), else the order's
 * occurrence. null = no schedule known (off-schedule or no order).
 */
function orderedWindowOf(
  sheets: Timesheet[],
  ctx: PlaceShiftContext,
): (date: string) => { start: number; end: number } | null {
  const windows = new Map<string, { start: number; end: number } | null>();
  return (date: string) => {
    if (windows.has(date)) return windows.get(date)!;
    const plan = sheets.find(
      (ts) => ts.place?.shiftDate === date && !ts.place?.offSchedule && ts.shiftPlan?.startAt && ts.shiftPlan?.endAt,
    )?.shiftPlan;
    const occ = ctx.occurrences?.find((o) => o.date === date);
    const w = plan
      ? { start: Math.floor(Date.parse(plan.startAt) / MIN_MS), end: Math.floor(Date.parse(plan.endAt) / MIN_MS) }
      : occ
        ? { start: Math.floor(occ.startMs / MIN_MS), end: Math.floor(occ.endMs / MIN_MS) }
        : null;
    windows.set(date, w && w.end > w.start ? w : null);
    return windows.get(date)!;
  };
}

const round2 = (v: number) => Math.round(v * 100) / 100;

const placeUnitKey = (clientId: string | undefined, jobId: string, placeId: string, date: string) =>
  JSON.stringify([clientId, jobId, 'place', placeId, date]);

/** One person's facts on one shift date of this order (arrival, leaving, decision). */
interface DayFacts {
  /** Server-accepted first start (minute), corrections included (M2). */
  arrival: number;
  /** Last checkout (minute); Infinity while a period is still open. */
  lastOut: number;
  shiftEnd?: TimesheetShiftEnd;
  /** The sheet the tail decision goes on (the latest one). */
  lastSheetId: string;
  wsKeys: Set<string>;
  offSite: boolean;
}

function dayFacts(shifts: WorkerShift[]): Map<string, DayFacts> {
  const out = new Map<string, DayFacts>();
  for (const ws of shifts) {
    const k = `${ws.workerId}|${ws.shiftDate}`;
    for (const ts of ws.members) {
      const entries = ts.entries ?? [];
      if (entries.length === 0) continue;
      const ins = entries.map((e) => Date.parse(e.checkInAt)).filter(Number.isFinite);
      if (ins.length === 0) continue;
      const open = entries.some((e) => !e.checkOutAt);
      const outs = entries.map((e) => Date.parse(e.checkOutAt ?? '')).filter(Number.isFinite);
      const arrival = Math.floor(Math.min(...ins) / MIN_MS);
      const lastOut = open ? Infinity : Math.floor(Math.max(...outs, -Infinity) / MIN_MS);
      const f = out.get(k) ?? {
        arrival: Infinity,
        lastOut: -Infinity,
        lastSheetId: ts.id,
        wsKeys: new Set<string>(),
        offSite: false,
      };
      if (arrival < f.arrival) f.arrival = arrival;
      if (lastOut >= f.lastOut) {
        f.lastOut = lastOut;
        f.lastSheetId = ts.id;
      }
      if (ts.shiftEnd && (!f.shiftEnd || ts.shiftEnd.decidedAt > f.shiftEnd.decidedAt)) f.shiftEnd = ts.shiftEnd;
      if (entries[0]?.onSite === false) f.offSite = true;
      f.wsKeys.add(ws.key);
      out.set(k, f);
    }
  }
  return out;
}

interface Acc {
  key: string;
  placeId: string | null;
  shiftDate: string;
  offSchedule: boolean;
  alpha: number;
  members: Map<string, number>;
  /** worker → first minute in this unit (for a stable order). */
  workers: Map<string, number>;
  /** Worker-shifts linked to this unit (α minutes or holding it). */
  links: Set<string>;
  bill: Set<number>;
  /**
   * Every billable worker-minute of the unit and its sheet (M-D9 classes).
   * Two people on one place at one minute are two entries — `bill` is a set.
   */
  billList: [number, string][];
  P: number;
  held: number;
  D: number;
  pRuns: number[];
  undecided?: { from: number; workerId: string; sheetId: string };
  offSite: boolean;
}

/** One run of the arithmetic on given segments (α and P/held/D), no β. */
function computeUnits(sheets: Timesheet[], ctx: PlaceShiftContext) {
  const shifts = workerShifts(sheets, ctx);
  const jobId = sheets[0]?.jobId ?? '';
  const clientId = sheets[0]?.clientId;
  const units = new Map<string, Acc>();
  const acc = (key: string, placeId: string | null, date: string): Acc => {
    let u = units.get(key);
    if (!u) {
      u = {
        key,
        placeId,
        shiftDate: date,
        offSchedule: true,
        alpha: 0,
        members: new Map(),
        workers: new Map(),
        links: new Set(),
        bill: new Set(),
        billList: [],
        P: 0,
        held: 0,
        D: 0,
        pRuns: [],
        offSite: false,
      };
      units.set(key, u);
    }
    return u;
  };

  // α
  for (const ws of shifts) {
    for (const m of ws.billable) {
      const p = ws.alpha.get(m) ?? null;
      const key = p ? placeUnitKey(clientId, jobId, p, ws.shiftDate) : JSON.stringify([clientId, jobId, 'unresolved', ws.key]);
      const u = acc(key, p, ws.shiftDate);
      if (!ws.offSchedule) u.offSchedule = false;
      u.alpha += 1;
      u.bill.add(m);
      const sheetId = ws.sheetAt.get(m) ?? ws.members[0].id;
      u.billList.push([m, sheetId]);
      u.members.set(sheetId, (u.members.get(sheetId) ?? 0) + 1);
      if (!u.workers.has(ws.workerId) || u.workers.get(ws.workerId)! > m) u.workers.set(ws.workerId, m);
      u.links.add(ws.key);
    }
  }

  // P / held / D — only with the order's segments (the money signal).
  if (ctx.segments) {
    const facts = dayFacts(shifts);
    const segs = ctx.segments
      .filter((s) => s.placeId)
      .map((s) => ({
        workerId: s.workerId,
        placeId: s.placeId!,
        start: Math.floor(Date.parse(s.startAt) / MIN_MS),
        end: Math.floor(Date.parse(s.endAt) / MIN_MS),
      }));
    const holderAt = (p: string, m: number) => segs.find((s) => s.placeId === p && m >= s.start && m < s.end)?.workerId;
    const placeOfAt = (w: string, m: number) => segs.find((s) => s.workerId === w && m >= s.start && m < s.end)?.placeId;
    // S per date: a sheet's own plan snapshot first, else the order's occurrence.
    const windowOf = orderedWindowOf(sheets, ctx);
    // Candidate units: α units on scheduled dates, plus places a person who
    // arrived that day held (or ended client-side) inside the window.
    const candidates = new Map<string, { placeId: string; date: string }>();
    for (const u of units.values())
      if (u.placeId && !u.offSchedule) candidates.set(u.key, { placeId: u.placeId, date: u.shiftDate });
    for (const [k, f] of facts) {
      const [workerId, date] = [k.slice(0, k.lastIndexOf('|')), k.slice(k.lastIndexOf('|') + 1)];
      const w = windowOf(date);
      if (!w) continue;
      for (const s of segs)
        if (s.workerId === workerId && s.start < w.end && s.end > w.start)
          candidates.set(placeUnitKey(clientId, jobId, s.placeId, date), { placeId: s.placeId, date });
      if (f.shiftEnd?.kind === 'client_ended') {
        const at = Math.floor(Date.parse(f.shiftEnd.at) / MIN_MS);
        const p = placeOfAt(workerId, at - 1);
        if (p) candidates.set(placeUnitKey(clientId, jobId, p, date), { placeId: p, date });
      }
    }
    const factsOf = (w: string, date: string) => facts.get(`${w}|${date}`);
    for (const [key, { placeId, date }] of candidates) {
      const w = windowOf(date);
      if (!w) continue;
      const existing = units.get(key);
      if (existing?.offSchedule) continue;
      const bill = existing?.bill ?? new Set<number>();
      let P = 0;
      let held = 0;
      let D = 0;
      const pRuns: number[] = [];
      const holders = new Set<string>();
      let undecided: Acc['undecided'];
      let offSite = false;
      for (let m = w.start; m < w.end; m += 1) {
        if (bill.has(m)) continue;
        const h = holderAt(placeId, m);
        let kind: 'P' | 'held' | 'D' = 'P';
        let who: string | undefined;
        if (h) {
          const f = factsOf(h, date);
          if (f && m >= f.arrival) {
            who = h;
            if (f.shiftEnd && m >= Math.floor(Date.parse(f.shiftEnd.at) / MIN_MS)) {
              kind = f.shiftEnd.kind === 'client_ended' ? 'held' : 'P';
            } else if (m >= f.lastOut) {
              kind = 'D';
              if (!undecided) undecided = { from: m, workerId: h, sheetId: f.lastSheetId };
            } else kind = 'held';
            if (f.offSite) offSite = true;
          }
        } else {
          // Time after a client-ended end on this place, nobody else on it: held.
          for (const [k, f] of facts) {
            if (!k.endsWith(`|${date}`) || f.shiftEnd?.kind !== 'client_ended') continue;
            const wid = k.slice(0, k.lastIndexOf('|'));
            const at = Math.floor(Date.parse(f.shiftEnd.at) / MIN_MS);
            // Only time the person no longer holds anywhere (their segment was
            // cut): where they still have a segment, that place holds it.
            if (m >= at && f.arrival <= at && placeOfAt(wid, at - 1) === placeId && !placeOfAt(wid, m)) {
              kind = 'held';
              who = wid;
              break;
            }
          }
        }
        if (kind === 'P') {
          P += 1;
          pRuns.push(m);
        } else {
          if (kind === 'held') held += 1;
          else D += 1;
          if (who) holders.add(who);
        }
      }
      if (!existing && held + D === 0) continue;
      const u = existing ?? acc(key, placeId, date);
      if (!existing) u.offSchedule = false;
      u.P = P;
      u.held = held;
      u.D = D;
      u.pRuns = pRuns;
      u.undecided = undecided;
      u.offSite = offSite;
      for (const wid of holders) {
        const f = factsOf(wid, date);
        for (const k of f?.wsKeys ?? []) u.links.add(k);
        // A holder with no minute here is still a member (C-2, SHOULD-5).
        for (const ws of shifts)
          if (f?.wsKeys.has(ws.key))
            for (const ts of ws.members) if (!u.members.has(ts.id)) u.members.set(ts.id, 0);
        if (!u.workers.has(wid)) u.workers.set(wid, Number.MAX_SAFE_INTEGER);
      }
    }
  }
  return { shifts, units, jobId, clientId };
}

function ranges(minutes: number[]): { from: string; to: string }[] {
  const out: { from: string; to: string }[] = [];
  let lo: number | null = null;
  let prev = 0;
  for (const m of minutes) {
    if (lo !== null && m === prev + 1) {
      prev = m;
      continue;
    }
    if (lo !== null) out.push({ from: minuteIso(lo), to: minuteIso(prev + 1) });
    lo = m;
    prev = m;
  }
  if (lo !== null) out.push({ from: minuteIso(lo), to: minuteIso(prev + 1) });
  return out;
}

/** Components (units sharing a worker-shift) and their unit results. */
function resultsOf(
  run: ReturnType<typeof computeUnits>,
  sheets: Timesheet[],
  ctx: PlaceShiftContext,
): { results: BillingUnitResult[]; componentOf: Map<string, string> } {
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    let r = k;
    while (parent.get(r) !== r) r = parent.get(r)!;
    parent.set(k, r);
    return r;
  };
  const byLink = new Map<string, string>();
  for (const u of run.units.values()) {
    parent.set(u.key, u.key);
    for (const l of u.links) {
      const other = byLink.get(l);
      if (other) {
        const a = find(u.key);
        const b = find(other);
        if (a !== b) parent.set(a < b ? b : a, a < b ? a : b);
      } else byLink.set(l, u.key);
    }
  }
  const order = new Map(sheets.map((ts, i) => [ts.id, i]));
  const sheetOrder = (id: string) => {
    const ts = sheets[order.get(id) ?? -1];
    return `${ts?.entries?.[0]?.checkInAt ?? ''}|${id}`;
  };
  const componentOf = new Map<string, string>();
  const results: BillingUnitResult[] = [];
  const windowOf = orderedWindowOf(sheets, ctx);
  const classifiers = new Map<string, (ms: number) => BillingHourClass>();
  for (const u of [...run.units.values()].sort((a, b) => a.key.localeCompare(b.key))) {
    const componentId = find(u.key);
    componentOf.set(u.key, componentId);
    const M = minimumMinutesFor(ctx.pricing, u.shiftDate);
    const A = u.alpha;
    const active = A > 0 || u.held + u.D > 0;
    const floor = active ? Math.max(A, M - u.P - u.D, 0) : 0;
    const full = active ? Math.max(A, M - u.P, 0) : 0;
    const t = floor - A;
    if (A === 0 && floor === 0 && u.held + u.D === 0) continue;
    const classes = classesOf(u, t, ctx, windowOf, classifiers);
    results.push({
      key: u.key,
      clientId: run.clientId,
      jobId: run.jobId,
      placeId: u.placeId,
      shiftDate: u.shiftDate,
      ...(u.offSchedule ? { offSchedule: true } : {}),
      componentId,
      minimumMinutes: M,
      actualMinutes: A,
      topUpMinutes: t,
      billedMinutes: floor,
      actualMinutesByClass: classes ? classes.actual : A > 0 ? { regular: A } : {},
      topUpMinutesByClass: classes ? classes.topUp : t > 0 ? { regular: t } : {},
      topUpBasis: t > 0 ? 'place' : 'none',
      ...(classes
        ? { classified: true, memberClasses: classes.members, topUpPeriods: classes.periods }
        : {}),
      members: [...u.members]
        .map(([sheetId, minutes]) => ({ sheetId, minutes }))
        .sort((a, b) => sheetOrder(a.sheetId).localeCompare(sheetOrder(b.sheetId))),
      workerIds: [...u.workers].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0])).map(([w]) => w),
      hours: round2(A / 60),
      billedHours: round2(floor / 60),
      topUpHours: round2(t / 60),
      ...(u.placeId ? {} : { unresolved: true }),
      providerShortfallMinutes: u.P,
      heldMinutes: u.held,
      undecidedMinutes: u.D,
      effectiveMinimumMinutes: Math.max(0, M - u.P),
      pendingTopUpMinutes: full - floor,
      shortfallRanges: ranges(u.pRuns),
      ...(u.undecided
        ? { undecided: { from: minuteIso(u.undecided.from), workerId: u.undecided.workerId, sheetId: u.undecided.sheetId } }
        : {}),
      ...(u.offSite ? { offSiteArrival: true } : {}),
    });
  }
  results.sort((a, b) => a.shiftDate.localeCompare(b.shiftDate) || a.key.localeCompare(b.key));
  return { results, componentOf };
}

const nonZero = (m: Map<BillingHourClass, number>): Partial<Record<BillingHourClass, number>> => {
  const out: Partial<Record<BillingHourClass, number>> = {};
  for (const cls of ['regular', 'night', 'weekend', 'public_holiday'] as const) {
    const v = m.get(cls) ?? 0;
    if (v !== 0) out[cls] = v;
  }
  return out;
};

/**
 * M-D9 — a unit's minutes by client class and its top-up spread (v3 B2/B3,
 * v4.3 g′, M-B). null when the order bills everything as 'regular'.
 *   A_k: billable minutes per class (outside S too); S_k: the ordered window
 *   per class (planned pause not subtracted); In_k: billable minutes inside S;
 *   P_k: RM24's shortfall minutes. T goes by g′_k = max(0, S_k − In_k − P_k)
 *   (fill g′ then S_k when |S| < M), by S_k when Σg′ = 0, by A_k without S.
 */
function classesOf(
  u: Acc,
  T: number,
  ctx: PlaceShiftContext,
  windowOf: (date: string) => { start: number; end: number } | null,
  cache: Map<string, (ms: number) => BillingHourClass>,
): {
  actual: Partial<Record<BillingHourClass, number>>;
  topUp: Partial<Record<BillingHourClass, number>>;
  members: Record<string, Partial<Record<BillingHourClass, number>>>;
  periods: TopUpPeriod[];
} | null {
  const classOf = classifierFor(ctx.pricing, u.shiftDate, cache);
  if (!classOf) return null;
  const version = coefficientRuleFor(ctx.pricing!.coefficientRule!, u.shiftDate);
  const S = u.offSchedule ? null : windowOf(u.shiftDate);
  const A = new Map<BillingHourClass, number>();
  const In = new Map<BillingHourClass, number>();
  const members: Record<string, Partial<Record<BillingHourClass, number>>> = {};
  // A_k and members count worker-minutes (as A does). In_k counts minutes of
  // the window: two people on one minute fill it once, so with P and the held
  // time it adds up to S_k (g′_k = what was held or left undecided).
  for (const [m, sheetId] of u.billList) {
    const cls = classOf(m);
    A.set(cls, (A.get(cls) ?? 0) + 1);
    const rec = (members[sheetId] ??= {});
    rec[cls] = (rec[cls] ?? 0) + 1;
  }
  if (S)
    for (const m of u.bill)
      if (m >= S.start && m < S.end) {
        const cls = classOf(m);
        In.set(cls, (In.get(cls) ?? 0) + 1);
      }
  const Sk = new Map<BillingHourClass, number>();
  const firstS = new Map<BillingHourClass, number>();
  if (S)
    for (let m = S.start; m < S.end; m += 1) {
      const cls = classOf(m);
      Sk.set(cls, (Sk.get(cls) ?? 0) + 1);
      if (!firstS.has(cls)) firstS.set(cls, m);
    }
  const Pk = new Map<BillingHourClass, number>();
  for (const m of u.pRuns) {
    const cls = classOf(m);
    Pk.set(cls, (Pk.get(cls) ?? 0) + 1);
  }
  const present = new Set<BillingHourClass>([...A.keys(), ...Sk.keys()]);
  const periods: TopUpPeriod[] = [...present]
    .sort((a, b) => CLASS_RANK[a] - CLASS_RANK[b])
    .map((cls) => ({
      cls,
      S: Sk.get(cls) ?? 0,
      In: In.get(cls) ?? 0,
      P: Pk.get(cls) ?? 0,
      A: A.get(cls) ?? 0,
      coefficientBp: agreedCoefficientBp(version, cls) ?? 10_000,
      // S-1: the earlier period of S; without S, regular < night < weekend < holiday.
      firstMinute: firstS.get(cls) ?? Number.MAX_SAFE_INTEGER - 4 + CLASS_RANK[cls],
    }));
  const topUp = T > 0 ? allocateTopUp(T, periods) : new Map<BillingHourClass, number>();
  // A unit with neither S nor A (cannot bill a top-up) keeps it regular rather than lose it.
  const placed = [...topUp.values()].reduce((s, v) => s + v, 0);
  if (T > 0 && placed < T) topUp.set('regular', (topUp.get('regular') ?? 0) + (T - placed));
  return { actual: nonZero(A), topUp: nonZero(topUp), members, periods };
}

function centsOf(units: BillingUnitResult[], pricing: JobPricingSnapshot | undefined): number {
  if (!pricing?.laborLines?.length) return units.reduce((s, u) => s + u.billedMinutes, 0);
  return priceLaborLines(units, pricing).reduce((s, l) => s + Math.round(l.totalEur * 100), 0);
}

/**
 * M-D9 — β's top-up minutes per class, laid onto the α units whose top-up
 * totals `t` the saving left. Each unit first keeps what it had in a class
 * (up to β's total there), then fills from what is left, cheapest class
 * first. Σ over units = β's per-class totals; each unit sums to its `t`.
 */
function distributeClasses(
  units: { key: string; t: number; own: Partial<Record<BillingHourClass, number>> }[],
  betaUnits: BillingUnitResult[],
  pricing: JobPricingSnapshot | undefined,
): Map<string, Partial<Record<BillingHourClass, number>>> {
  const pool = new Map<BillingHourClass, number>();
  for (const b of betaUnits)
    for (const [cls, v] of Object.entries(b.topUpMinutesByClass) as [BillingHourClass, number][])
      pool.set(cls, (pool.get(cls) ?? 0) + v);
  const rule = pricing?.coefficientRule;
  const bp = (cls: BillingHourClass, date: string) =>
    (rule ? agreedCoefficientBp(coefficientRuleFor(rule, date), cls) : null) ?? 10_000;
  const date = betaUnits[0]?.shiftDate ?? '';
  const order = (['regular', 'night', 'weekend', 'public_holiday'] as const)
    .slice()
    .sort((a, b) => bp(a, date) - bp(b, date) || CLASS_RANK[a] - CLASS_RANK[b]);
  const out = new Map<string, Map<BillingHourClass, number>>();
  const left = new Map<string, number>();
  const sorted = [...units].sort((a, b) => a.key.localeCompare(b.key));
  for (const u of sorted) {
    const m = new Map<BillingHourClass, number>();
    let rest = Math.max(0, u.t);
    for (const cls of order) {
      const take = Math.min(rest, u.own[cls] ?? 0, pool.get(cls) ?? 0);
      if (take > 0) {
        m.set(cls, take);
        pool.set(cls, (pool.get(cls) ?? 0) - take);
        rest -= take;
      }
    }
    out.set(u.key, m);
    left.set(u.key, rest);
  }
  for (const u of sorted) {
    const m = out.get(u.key)!;
    let rest = left.get(u.key)!;
    for (const cls of order) {
      const take = Math.min(rest, pool.get(cls) ?? 0);
      if (take > 0) {
        m.set(cls, (m.get(cls) ?? 0) + take);
        pool.set(cls, (pool.get(cls) ?? 0) - take);
        rest -= take;
      }
    }
    // Defensive: β's pool short (a unit outside the β view) — the rest bills regular.
    if (rest > 0) m.set('regular', (m.get('regular') ?? 0) + rest);
  }
  return new Map([...out].map(([k, m]) => [k, nonZero(m)]));
}

/**
 * β — ONE counterfactual run per component, every mover at once (M-C): the
 * moved person's segments on other places become their Home place wherever
 * nobody else's segment holds Home at that minute; α, held and P are then
 * computed again on those segments (a vacated place loses its holder → P).
 */
function counterfactualSegments(
  segments: readonly Segment[],
  movers: WorkerShift[],
  windowOf: (ws: WorkerShift) => { start: number; end: number },
): Segment[] {
  const moverHome = new Map<string, { home: string; lo: number; hi: number }[]>();
  for (const ws of movers) {
    if (!ws.home) continue;
    const w = windowOf(ws);
    moverHome.set(ws.workerId, [...(moverHome.get(ws.workerId) ?? []), { home: ws.home, lo: w.start, hi: w.end }]);
  }
  const out: Segment[] = [];
  for (const s of segments) {
    const homes = moverHome.get(s.workerId);
    const target = homes?.find((h) => h.home !== s.placeId);
    if (!homes || !target || !s.placeId) {
      out.push(s);
      continue;
    }
    const start = Math.floor(Date.parse(s.startAt) / MIN_MS);
    const end = Math.floor(Date.parse(s.endAt) / MIN_MS);
    const othersOnHome = (m: number) =>
      segments.some(
        (o) =>
          o.workerId !== s.workerId &&
          o.placeId === target.home &&
          m >= Math.floor(Date.parse(o.startAt) / MIN_MS) &&
          m < Math.floor(Date.parse(o.endAt) / MIN_MS),
      );
    let runStart = start;
    let runPlace: string | undefined;
    let i = 0;
    const flush = (to: number) => {
      if (to > runStart && runPlace)
        out.push({ ...s, id: `${s.id}~β${i++}`, placeId: runPlace, startAt: minuteIso(runStart), endAt: minuteIso(to) });
    };
    for (let m = start; m < end; m += 1) {
      const relabel = m >= target.lo && m < target.hi && !othersOnHome(m);
      const p = relabel ? target.home : s.placeId;
      if (p !== runPlace) {
        flush(m);
        runStart = m;
        runPlace = p;
      }
    }
    flush(end);
  }
  return out;
}

/**
 * Billing units of ONE order's sheets. Callers pass every member of every
 * shift they bill or show (finally rejected sheets are ignored here).
 *
 * Per unit u (place p, shift date d), M = the minimum in minutes (v4–v4.3):
 *   A = billable minutes (α); within the ordered window S each other minute
 *   is P (RM24 did not provide it: no holder, not yet arrived, released or
 *   marked provider_shortfall), held (holder there, pause or a client-ended
 *   end) or D (after the holder's last checkout, segment going on, no
 *   shiftEnd decision). Active ⇔ A > 0 or something was held.
 *   billed now = max(A, M − P − D, 0); after a decision max(A, M − P, 0).
 */
export function placeShiftBilling(sheets: Timesheet[], ctx: PlaceShiftContext): PlaceShiftBilling {
  const alphaRun = computeUnits(sheets, ctx);
  const { results: alphaResults, componentOf } = resultsOf(alphaRun, sheets, ctx);
  const components: PlaceShiftBilling['components'] = new Map();
  let results = alphaResults;

  if (ctx.segments) {
    const byComponent = new Map<string, BillingUnitResult[]>();
    for (const u of alphaResults) byComponent.set(u.componentId, [...(byComponent.get(u.componentId) ?? []), u]);
    for (const [componentId, list] of byComponent) {
      const alphaCents = centsOf(list, ctx.pricing);
      const sheetIds = new Set(list.flatMap((u) => u.members.map((m) => m.sheetId)));
      const unitKeys = new Set(list.map((u) => u.key));
      const movers = alphaRun.shifts.filter(
        (ws) =>
          ws.home &&
          ws.members.some((ts) => sheetIds.has(ts.id)) &&
          ws.billable.some((m) => {
            const p = ws.alpha.get(m);
            return p && p !== ws.home;
          }),
      );
      if (movers.length === 0 || list.length < 2) {
        components.set(componentId, { alphaCents, betaCents: null, usedBeta: false });
        continue;
      }
      const cf = counterfactualSegments(ctx.segments, movers, (ws) => {
        const occ = ctx.occurrences?.find((o) => o.date === ws.shiftDate);
        const lo = Math.min(occ ? Math.floor(occ.startMs / MIN_MS) : Infinity, ws.billable[0] ?? Infinity);
        const hi = Math.max(occ ? Math.floor(occ.endMs / MIN_MS) : -Infinity, (ws.billable[ws.billable.length - 1] ?? -Infinity) + 1);
        return { start: lo, end: hi };
      });
      const betaRun = resultsOf(computeUnits(sheets, { ...ctx, segments: cf }), sheets, { ...ctx, segments: cf });
      const betaUnits = betaRun.results.filter(
        (u) => unitKeys.has(u.key) || u.members.some((m) => sheetIds.has(m.sheetId)),
      );
      const betaCents = centsOf(betaUnits, ctx.pricing);
      // M-D9 — compared in CENTS, coefficients included: β can be fewer
      // minutes and still more money (its top-up in dearer classes); then α
      // bills. A tie keeps α.
      const usedBeta = betaCents < alphaCents;
      components.set(componentId, { alphaCents, betaCents, usedBeta });
      if (usedBeta) {
        // SHOULD-B — the units stay as worked (real places, members, P,
        // held); the counterfactual's saving comes off their top-ups,
        // highest first, so the component bills exactly what β would.
        let saving =
          list.reduce((t, u) => t + u.billedMinutes, 0) - betaUnits.reduce((t, u) => t + u.billedMinutes, 0);
        const reduced = new Map<string, number>();
        for (const u of [...list].sort((a, b) => b.topUpMinutes - a.topUpMinutes || a.key.localeCompare(b.key))) {
          const cut = Math.min(saving, u.topUpMinutes);
          reduced.set(u.key, cut);
          saving -= cut;
        }
        // M-D9 — the component's top-up classes are β's (the actual minutes
        // and their classes are the same in both runs), so it bills β's money.
        const byClass = list.some((u) => u.classified)
          ? distributeClasses(
              list.map((u) => ({ key: u.key, t: u.topUpMinutes - (reduced.get(u.key) ?? 0), own: u.topUpMinutesByClass })),
              betaUnits,
              ctx.pricing,
            )
          : null;
        results = results.map((u) => {
          if (u.componentId !== componentId) return u;
          const t = u.topUpMinutes - (reduced.get(u.key) ?? 0);
          return {
            ...u,
            topUpMinutes: t,
            billedMinutes: u.actualMinutes + t,
            topUpMinutesByClass: byClass ? (byClass.get(u.key) ?? {}) : t > 0 ? { regular: t } : {},
            topUpBasis: 'move_capped' as const,
            billedHours: round2((u.actualMinutes + t) / 60),
            topUpHours: round2(t / 60),
          };
        });
      }
    }
    results.sort((a, b) => a.shiftDate.localeCompare(b.shiftDate) || a.key.localeCompare(b.key));
  }
  void componentOf;

  // Rows: each sheet's minutes; a unit's top-up on its last positive member
  // (else its last member — a unit held without work).
  const rows = new Map<string, BillingRow>();
  const rowOf = (ts: Timesheet) => {
    const k = `${ts.jobId}/${ts.id}`;
    let r = rows.get(k);
    if (!r) {
      r = { hours: 0, billedHours: 0, breakMinutes: 0, minutes: 0, topUpMinutes: 0 };
      rows.set(k, r);
    }
    return r;
  };
  const sheetById = new Map(sheets.map((ts) => [ts.id, ts]));
  for (const ws of alphaRun.shifts) {
    for (const ts of ws.members) rowOf(ts);
    for (const m of ws.pauseMinutes) {
      const ts = sheetById.get(ws.sheetAt.get(m) ?? '');
      if (ts) rowOf(ts).breakMinutes += 1;
    }
  }
  for (const u of results) {
    for (const mem of u.members) {
      const ts = sheetById.get(mem.sheetId);
      if (ts) rowOf(ts).minutes += mem.minutes;
    }
    const last = [...u.members].reverse().find((mem) => mem.minutes > 0) ?? u.members[u.members.length - 1];
    const ts = last ? sheetById.get(last.sheetId) : undefined;
    if (ts) rowOf(ts).topUpMinutes += u.topUpMinutes;
  }
  // M-D9 — each row's minutes in coefficient terms (the top-up on the same row as above).
  const anyClassified = results.some((u) => u.classified);
  const weighted = new Map<string, number>();
  if (anyClassified) {
    const rule = ctx.pricing?.coefficientRule;
    for (const u of results) {
      const version = rule ? coefficientRuleFor(rule, u.shiftDate) : undefined;
      const value = (m: Partial<Record<BillingHourClass, number>>) =>
        CLASS_LINE_ORDER.reduce(
          (s, c) => s + (m[c] ?? 0) * ((agreedCoefficientBp(version, c) ?? 10_000) / 10_000),
          0,
        );
      const keyOf = (id: string) => `${sheetById.get(id)?.jobId}/${id}`;
      for (const mem of u.members) {
        const worth = u.memberClasses?.[mem.sheetId] ? value(u.memberClasses[mem.sheetId]) : mem.minutes;
        weighted.set(keyOf(mem.sheetId), (weighted.get(keyOf(mem.sheetId)) ?? 0) + worth);
      }
      const last = [...u.members].reverse().find((mem) => mem.minutes > 0) ?? u.members[u.members.length - 1];
      if (last) weighted.set(keyOf(last.sheetId), (weighted.get(keyOf(last.sheetId)) ?? 0) + value(u.topUpMinutesByClass));
    }
  }
  for (const [k, r] of rows) {
    r.hours = round2(r.minutes / 60);
    r.billedHours = round2((r.minutes + r.topUpMinutes) / 60);
    if (anyClassified) r.weightedBilledHours = (weighted.get(k) ?? 0) / 60;
  }
  return {
    units: results,
    rows,
    breakMinutesByShift: new Map(alphaRun.shifts.map((ws) => [ws.key, ws.breakMinutes])),
    components,
  };
}

// ── Billing a selection (C-2) ────────────────────────────────────────────────

export type ComponentBlockReason =
  | 'review'
  | 'rejected'
  | 'unapproved'
  | 'ongoing_shift'
  | 'incomplete_shift'
  | 'unresolved_place'
  | 'component_locked'
  /** A billed day of the component has no stored billing unit to read (never recomputed). */
  | 'billed_unit_missing'
  /**
   * v4.3 — a holder left before the ordered end, the reason is not decided,
   * and the decision would change the money: RM24 decides (`decideShiftEnd`)
   * before this component bills. A billing gate only — approval and payroll
   * are untouched; other components bill.
   */
  | 'early_end_undecided';

const isBilledElsewhere = (ts: Timesheet, invoiceId?: string) =>
  !!ts.invoiceId || ts.status === 'invoiced' || (!!ts.draftInvoiceId && ts.draftInvoiceId !== invoiceId);

const lockResolved = (ts: Timesheet) =>
  (ts.review ?? []).some((r) => r.reason === 'component_locked' && !!r.resolvedAt) &&
  !(ts.review ?? []).some((r) => r.reason === 'component_locked' && !r.resolvedAt);

export interface ComponentDecision {
  componentId: string;
  sheetIds: string[];
  /** Why the component cannot be billed with this selection; undefined = billable. */
  block?: ComponentBlockReason;
  /** The component already has billed days (C-2). */
  locked: boolean;
}

/**
 * Which components a selection touches and whether each can be billed.
 * A component bills whole or not at all: every member must be selected (or
 * already billed), approved, settled and over. `blockOf` is the legacy
 * readiness test (`shiftBillingBlockReason`) applied to the component.
 */
export function componentDecisions(
  all: Timesheet[],
  selected: ReadonlySet<string>,
  ctx: PlaceShiftContext,
  blockOf: (members: Timesheet[], selected: ReadonlySet<string>) => ComponentBlockReason | undefined,
  invoiceId?: string,
): { billing: PlaceShiftBilling; decisions: ComponentDecision[] } {
  const live = all.filter((ts) => !(ts.status === 'rejected' && ts.finalRejection));
  const billing = placeShiftBilling(live, ctx);
  const sheetsOf = new Map<string, Set<string>>();
  for (const u of billing.units) {
    const set = sheetsOf.get(u.componentId) ?? new Set<string>();
    for (const m of u.members) set.add(m.sheetId);
    sheetsOf.set(u.componentId, set);
  }
  // A sheet with no billable minute (all pause, or empty) still belongs to its worker-shift.
  const byId = new Map(live.map((ts) => [ts.id, ts]));
  const componentOfSheet = new Map<string, string>();
  for (const [c, ids] of sheetsOf) for (const id of ids) componentOfSheet.set(id, c);
  for (const ts of live) {
    if (componentOfSheet.has(ts.id)) continue;
    const sibling = live.find(
      (o) => o.id !== ts.id && timesheetShiftKey(o) === timesheetShiftKey(ts) && componentOfSheet.has(o.id),
    );
    const c = sibling ? componentOfSheet.get(sibling.id)! : `sheet:${ts.id}`;
    componentOfSheet.set(ts.id, c);
    sheetsOf.set(c, new Set([...(sheetsOf.get(c) ?? []), ts.id]));
  }
  const decisions: ComponentDecision[] = [];
  for (const [componentId, ids] of sheetsOf) {
    const members = [...ids].map((id) => byId.get(id)!).filter(Boolean);
    if (!members.some((ts) => selected.has(ts.id))) continue;
    const billed = members.filter((ts) => isBilledElsewhere(ts, invoiceId));
    const open = members.filter((ts) => !isBilledElsewhere(ts, invoiceId));
    let block = blockOf(open, selected);
    // An unresolved place only holds money it could change (minimum > 0).
    const unresolved = billing.units.some(
      (u) => u.componentId === componentId && u.unresolved && u.minimumMinutes > 0,
    );
    if (!block && unresolved) block = 'unresolved_place';
    const locked = billed.length > 0;
    // The early-end gate only where it can be lifted: a unit with an open
    // (not yet billed) member, in a component nothing of which is billed —
    // `decideShiftEnd` refuses once anything is (COMPONENT_BILLED), so a
    // pending top-up there would hold the component for ever. A billed part's
    // undecided tail is simply not billed (C-2 never recomputes it).
    const openIds = new Set(open.map((ts) => ts.id));
    if (
      !block &&
      !locked &&
      billing.units.some(
        (u) =>
          u.componentId === componentId &&
          u.pendingTopUpMinutes > 0 &&
          u.members.some((m) => openIds.has(m.sheetId)),
      )
    )
      block = 'early_end_undecided';
    if (!block && locked && open.some((ts) => !lockResolved(ts))) block = 'component_locked';
    decisions.push({ componentId, sheetIds: members.map((ts) => ts.id), block, locked });
  }
  return { billing, decisions };
}

/**
 * The billing units a selection bills.
 *
 * A clean component bills as computed. A component with days already billed
 * (resolved `component_locked`) bills only what the late days ADD, and the
 * billed part is READ from the billing units stored on the invoices that
 * billed it (`storedBilled`) — never recomputed from today's sheets, which a
 * post-invoice edit or a segment change may have moved since:
 *
 *   - a unit already billed (stored key): pooled PER UNIT — its late minutes
 *     first fill what that unit billed; only time beyond it is added;
 *   - a unit the stored record does not have: billed with its own minimum,
 *     unless the late minutes belong to a worker-shift already in the billed
 *     record (a move inside the shift): those are added without a minimum.
 * One increment per unit (`${key}#increment`).
 *
 * No component ever bills a negative quantity: the late days' minutes split
 * into `actualMinutes` (billed) and `coveredByBilledMinimumMinutes`.
 * Returns `missing` = components with billed days but no stored record — the
 * caller holds them (`billed_unit_missing`), nothing is computed.
 */
export function unitsForSelection(
  all: Timesheet[],
  selected: ReadonlySet<string>,
  ctx: PlaceShiftContext,
  decisions: ComponentDecision[],
  billing: PlaceShiftBilling,
  invoiceId: string | undefined,
  storedBilled: readonly InvoiceBillingUnit[],
): { units: BillingUnitResult[]; missing: string[] } {
  void ctx; // the units already carry their minimum and shortfall
  const out: BillingUnitResult[] = [];
  const missing: string[] = [];
  const live = all.filter((ts) => !(ts.status === 'rejected' && ts.finalRejection));
  const byId = new Map(live.map((ts) => [ts.id, ts]));
  for (const d of decisions) {
    const units = billing.units.filter((u) => u.componentId === d.componentId);
    if (!d.locked) {
      out.push(...units);
      continue;
    }
    const billedIds = new Set(
      d.sheetIds.filter((id) => byId.get(id) && isBilledElsewhere(byId.get(id)!, invoiceId)),
    );
    const stored = storedBilled.filter((u) => u.members.some((m) => billedIds.has(m.sheetId)));
    if (stored.length === 0) {
      missing.push(d.componentId);
      continue;
    }
    // The stored record per base unit key (an earlier increment adds to its unit).
    const base = (k: string) => k.replace(/#increment$/, '');
    const storedBy = new Map<string, { actual: number; billed: number; topUp: Map<BillingHourClass, number> }>();
    for (const u of stored) {
      const acc = storedBy.get(base(u.key)) ?? { actual: 0, billed: 0, topUp: new Map<BillingHourClass, number>() };
      // M-D9 — top-up by class still unconsumed: billed, less what earlier late days offset.
      for (const [cls, v] of Object.entries(u.topUpMinutesByClass ?? {}) as [BillingHourClass, number][])
        acc.topUp.set(cls, (acc.topUp.get(cls) ?? 0) + v);
      for (const [cls, v] of Object.entries(u.coveredMinutesByClass ?? {}) as [BillingHourClass, number][])
        acc.topUp.set(cls, (acc.topUp.get(cls) ?? 0) - v);
      // An increment stored only its billed part as actual; its covered minutes were worked too.
      acc.actual += u.actualMinutes + (u.coveredByBilledMinimumMinutes ?? 0);
      acc.billed += u.billedMinutes;
      storedBy.set(base(u.key), acc);
    }
    // Worker-shifts already in the billed record: their minutes on another
    // place are a continuation (a move) and never earn a second minimum (Q4).
    const storedShifts = new Set(
      stored
        .flatMap((u) => u.members.map((m) => byId.get(m.sheetId)))
        .filter((ts): ts is Timesheet => !!ts)
        .map((ts) => timesheetShiftKey(ts)),
    );
    for (const u of units) {
      const added = u.members.filter((m) => selected.has(m.sheetId) && !billedIds.has(m.sheetId));
      const a = added.reduce((sum, m) => sum + m.minutes, 0);
      if (a === 0) continue;
      const rec = storedBy.get(u.key);
      let delta: number;
      if (rec) {
        // Pooled PER UNIT: the late minutes first fill what this unit already billed.
        delta = Math.max(0, rec.actual + a - rec.billed);
      } else {
        const cont = added
          .filter((m) => {
            const ts = byId.get(m.sheetId);
            return !!ts && storedShifts.has(timesheetShiftKey(ts));
          })
          .reduce((sum, m) => sum + m.minutes, 0);
        const fresh = a - cont;
        // v4: a fresh unit's minimum is reduced by RM24's shortfall (and an
        // undecided tail, which bills only once decided).
        const floorMin = Math.max(0, u.effectiveMinimumMinutes - u.undecidedMinutes);
        delta = cont + (fresh > 0 ? Math.max(fresh, floorMin) : 0);
      }
      const actualBilled = Math.min(a, delta);
      const t = delta - actualBilled;
      const covered = a - actualBilled;
      const classes = u.classified ? incrementClasses(u, added, covered, t, rec?.topUp ?? new Map()) : null;
      out.push({
        ...u,
        key: `${u.key}#increment`,
        actualMinutes: actualBilled,
        topUpMinutes: t,
        billedMinutes: delta,
        actualMinutesByClass: classes ? classes.actual : actualBilled > 0 ? { regular: actualBilled } : {},
        topUpMinutesByClass: classes ? classes.topUp : t > 0 ? { regular: t } : {},
        topUpBasis: t > 0 ? 'place' : 'none',
        members: added,
        hours: round2(actualBilled / 60),
        billedHours: round2(delta / 60),
        topUpHours: round2(t / 60),
        increment: true,
        ...(covered > 0 ? { coveredByBilledMinimumMinutes: covered } : {}),
        ...(classes && covered > 0 ? { coveredMinutesByClass: classes.covered } : {}),
      });
    }
  }
  return { units: out, missing };
}

/**
 * Largest remainder of `amount` over the classes by their minutes; a tie goes
 * to the lower coefficient, then the earlier period (the allocation's own rule).
 */
function spreadByMinutes(
  amount: number,
  minutes: Map<BillingHourClass, number>,
  periods: readonly TopUpPeriod[],
): Map<BillingHourClass, number> {
  const out = new Map<BillingHourClass, number>();
  const total = [...minutes.values()].reduce((s, v) => s + v, 0);
  if (amount <= 0 || total <= 0) return out;
  const tie = (c: BillingHourClass) => periods.find((p) => p.cls === c);
  const shares = [...minutes].map(([cls, v]) => {
    const exact = (amount * v) / total;
    return { cls, floor: Math.floor(exact), rest: exact - Math.floor(exact) };
  });
  let left = amount - shares.reduce((s, x) => s + x.floor, 0);
  for (const x of shares) out.set(x.cls, x.floor);
  for (const x of [...shares].sort(
    (a, b) =>
      b.rest - a.rest ||
      (tie(a.cls)?.coefficientBp ?? 10_000) - (tie(b.cls)?.coefficientBp ?? 10_000) ||
      (tie(a.cls)?.firstMinute ?? 0) - (tie(b.cls)?.firstMinute ?? 0),
  )) {
    if (left <= 0) break;
    out.set(x.cls, (out.get(x.cls) ?? 0) + 1);
    left -= 1;
  }
  return out;
}

/**
 * M-D9 — an increment's classes: the added sheets' minutes by class, less the
 * part already covered by the billed minimum, and a fresh top-up spread by the
 * unit's own periods.
 *
 * PENDING MARTI'S CONFIRMATION (dormant behind `placeShiftEnabled`; a switch
 * gate): which late minutes does the billed minimum cover? Neutral derivation —
 * a late minute first offsets top-up of the SAME class already billed on the
 * stored unit (the client already paid that class); what coverage remains is
 * spread over the remaining classes in proportion to their minutes (largest
 * remainder, the allocation's tie rule). No class is preferred for being dear
 * or cheap.
 */
function incrementClasses(
  u: BillingUnitResult,
  added: { sheetId: string; minutes: number }[],
  covered: number,
  t: number,
  storedTopUp: ReadonlyMap<BillingHourClass, number>,
): {
  actual: Partial<Record<BillingHourClass, number>>;
  topUp: Partial<Record<BillingHourClass, number>>;
  covered: Partial<Record<BillingHourClass, number>>;
} {
  const late = new Map<BillingHourClass, number>();
  for (const m of added)
    for (const [cls, v] of Object.entries(u.memberClasses?.[m.sheetId] ?? {}) as [BillingHourClass, number][])
      late.set(cls, (late.get(cls) ?? 0) + v);
  const coveredBy = new Map<BillingHourClass, number>();
  let cover = covered;
  for (const cls of CLASS_LINE_ORDER) {
    const take = Math.min(cover, late.get(cls) ?? 0, storedTopUp.get(cls) ?? 0);
    if (take <= 0) continue;
    coveredBy.set(cls, take);
    cover -= take;
  }
  const remaining = new Map(
    [...late].map(([cls, v]) => [cls, v - (coveredBy.get(cls) ?? 0)] as const).filter(([, v]) => v > 0),
  );
  for (const [cls, v] of spreadByMinutes(cover, remaining, u.topUpPeriods ?? []))
    coveredBy.set(cls, (coveredBy.get(cls) ?? 0) + v);
  const billed = new Map([...late].map(([cls, v]) => [cls, v - (coveredBy.get(cls) ?? 0)] as const));
  const topUp = t > 0 ? allocateTopUp(t, u.topUpPeriods ?? []) : new Map<BillingHourClass, number>();
  const placed = [...topUp.values()].reduce((s, v) => s + v, 0);
  if (t > 0 && placed < t) topUp.set('regular', (topUp.get('regular') ?? 0) + (t - placed));
  return { actual: nonZero(billed), topUp: nonZero(topUp), covered: nonZero(coveredBy) };
}

/** Invoice ids holding a component's billed days (to read their stored units). */
export function billedInvoiceIds(sheets: Timesheet[], invoiceId?: string): string[] {
  const ids = new Set<string>();
  for (const ts of sheets) {
    if (ts.invoiceId) ids.add(ts.invoiceId);
    else if (ts.draftInvoiceId && ts.draftInvoiceId !== invoiceId) ids.add(ts.draftInvoiceId);
  }
  return [...ids];
}

// ── D9: how the top-up spreads over hour classes (pure, not used until D9) ──

export interface TopUpPeriod {
  cls: BillingHourClass;
  /** S_k — ordered minutes in this class. */
  S: number;
  /** In_k — the unit's billable minutes inside S ∩ k. */
  In: number;
  /** P_k — RM24 shortfall minutes in S ∩ k. */
  P: number;
  /** A_k — the unit's billable minutes in this class (incl. outside S). */
  A: number;
  coefficientBp: number;
  /** First minute of S ∩ k (tie-break: earlier period). */
  firstMinute: number;
}

/**
 * M-D9 (v4 + v4.2 M-B) — split a unit's top-up T (whole minutes) over hour
 * classes. Weights g′_k = max(0, S_k − In_k − P_k): the ordered time not
 * worked and not RM24's shortfall. With |S| ≥ M, Σg′ ≥ T and T goes by g′
 * (T_k ≤ g′_k); with |S| < M the g′ are filled and the rest goes by S_k.
 * Σg′ = 0 → by S_k; no S → by A_k. Largest remainder; a tie goes to the
 * lower coefficient, then the earlier period. Σ T_k = T exactly. An undecided
 * tail counts as held here until decided (an undecided unit is not priced).
 */
export function allocateTopUp(T: number, periods: TopUpPeriod[]): Map<BillingHourClass, number> {
  const out = new Map<BillingHourClass, number>(periods.map((p) => [p.cls, 0]));
  if (T <= 0 || periods.length === 0) return out;
  const split = (amount: number, weight: (p: TopUpPeriod) => number) => {
    const total = periods.reduce((s, p) => s + weight(p), 0);
    if (amount <= 0 || total <= 0) return amount;
    const shares = periods.map((p) => {
      const exact = (amount * weight(p)) / total;
      return { p, floor: Math.floor(exact), rest: exact - Math.floor(exact) };
    });
    let left = amount - shares.reduce((s, x) => s + x.floor, 0);
    for (const x of shares) out.set(x.p.cls, (out.get(x.p.cls) ?? 0) + x.floor);
    shares
      .slice()
      .sort(
        (a, b) =>
          b.rest - a.rest ||
          a.p.coefficientBp - b.p.coefficientBp ||
          a.p.firstMinute - b.p.firstMinute,
      )
      .forEach((x) => {
        if (left > 0 && x.rest > 0) {
          out.set(x.p.cls, (out.get(x.p.cls) ?? 0) + 1);
          left -= 1;
        }
      });
    return left;
  };
  const g = (p: TopUpPeriod) => Math.max(0, p.S - p.In - p.P);
  const sumG = periods.reduce((s, p) => s + g(p), 0);
  const sumS = periods.reduce((s, p) => s + p.S, 0);
  if (sumS === 0) {
    split(T, (p) => p.A);
    return out;
  }
  if (sumG >= T) {
    split(T, g);
    return out;
  }
  // |S| < M: fill every g′, the rest by S_k.
  for (const p of periods) out.set(p.cls, (out.get(p.cls) ?? 0) + g(p));
  split(T - sumG, (p) => p.S);
  return out;
}

// ── Money (B4) ───────────────────────────────────────────────────────────────

/** Line order of the classes on a document (lowest priority first). */
const CLASS_LINE_ORDER: readonly BillingHourClass[] = [...CLIENT_HOUR_CLASS_PRIORITY].reverse();

/** Integer cents: roundHalfUp(minutes × rate cents × bp / 600 000). */
export function laborCents(minutes: number, hourlyRateEur: number, coefficientBp = 10_000): number {
  const rateCents = Math.round(hourlyRateEur * 100);
  const raw = (minutes * rateCents * coefficientBp) / 600_000;
  // Half away from zero, on a value that is exact in integers × 1/600 000.
  return Math.sign(raw) * Math.floor(Math.abs(raw) + 0.5 + 1e-9);
}

/**
 * The labor lines of a place-and-shift order: per labor line, one row per
 * (component, hour class, coefficient) with the minutes of every unit summed,
 * priced once (B4). Every labor line bills the units' time (one line per
 * trade in practice) — the same convention as the legacy builder.
 *
 * M-D9: a class's coefficient comes ONLY from the order's `coefficientRule`
 * version for each unit's shift date — never from the catalog (F10). The
 * agreement is client-wide: it covers every labor line of the order, also one
 * added after it was confirmed. Without a rule the output is the D4b one, unchanged.
 */
export function priceLaborLines(
  units: BillingUnitResult[],
  snapshot: Pick<JobPricingSnapshot, 'laborLines'> & Partial<Pick<JobPricingSnapshot, 'coefficientRule'>>,
  labelPrefix = '',
): InvoiceLine[] {
  const pfx = labelPrefix ? `${labelPrefix} · ` : '';
  const rule = snapshot.coefficientRule;
  const lines: InvoiceLine[] = [];
  for (const l of snapshot.laborLines ?? []) {
    for (const component of ['actual', 'minimum_topup'] as const) {
      const groups = new Map<string, { cls: BillingHourClass; bp: number | null; minutes: number; refs: string[] }>();
      for (const u of units) {
        const byClass = component === 'actual' ? u.actualMinutesByClass : u.topUpMinutesByClass;
        const version = rule ? coefficientRuleFor(rule, u.shiftDate) : undefined;
        for (const cls of CLASS_LINE_ORDER) {
          const minutes = byClass[cls] ?? 0;
          if (minutes === 0) continue;
          const bp = agreedCoefficientBp(version, cls);
          const c: BillingHourClass = bp != null ? cls : 'regular';
          const key = `${c}|${bp ?? ''}`;
          const g = groups.get(key) ?? { cls: c, bp, minutes: 0, refs: [] };
          g.minutes += minutes;
          if (!g.refs.includes(u.key)) g.refs.push(u.key);
          groups.set(key, g);
        }
      }
      const ordered = [...groups.values()].sort(
        (a, b) => CLASS_LINE_ORDER.indexOf(a.cls) - CLASS_LINE_ORDER.indexOf(b.cls) || (a.bp ?? 0) - (b.bp ?? 0),
      );
      for (const g of ordered) {
        if (g.minutes === 0) continue;
        const bp = g.bp ?? 10_000;
        const cents = laborCents(g.minutes, l.hourlyRateEur, bp);
        lines.push({
          kind: 'labor',
          label: `${pfx}${l.skillId}`,
          quantity: round2(g.minutes / 60),
          quantityMinutes: g.minutes,
          unit: 'h',
          unitPriceEur: round2((l.hourlyRateEur * bp) / 10_000),
          totalEur: cents / 100,
          component,
          hourClass: g.cls,
          ...(g.bp != null ? { coefficient: g.bp / 10_000, baseUnitPriceEur: round2(l.hourlyRateEur) } : {}),
          unitRef: g.refs,
        });
      }
    }
  }
  return lines;
}

/** The record of a unit kept on the invoice (`Invoice.billingUnits`). */
export function invoiceBillingUnit(u: BillingUnitResult): InvoiceBillingUnit {
  return {
    key: u.key,
    jobId: u.jobId,
    placeId: u.placeId,
    shiftDate: u.shiftDate,
    componentId: u.componentId,
    minimumMinutes: u.minimumMinutes,
    actualMinutes: u.actualMinutes,
    topUpMinutes: u.topUpMinutes,
    billedMinutes: u.billedMinutes,
    topUpBasis: u.topUpBasis,
    ...(u.offSchedule ? { offSchedule: true } : {}),
    members: u.members,
    ...(u.increment ? { increment: true } : {}),
    ...(u.coveredByBilledMinimumMinutes
      ? { coveredByBilledMinimumMinutes: u.coveredByBilledMinimumMinutes }
      : {}),
    ...(u.providerShortfallMinutes ? { providerShortfallMinutes: u.providerShortfallMinutes } : {}),
    ...(u.classified
      ? {
          actualMinutesByClass: u.actualMinutesByClass,
          topUpMinutesByClass: u.topUpMinutesByClass,
          ...(u.coveredMinutesByClass ? { coveredMinutesByClass: u.coveredMinutesByClass } : {}),
        }
      : {}),
  };
}

// ── The plan (estimate and proforma) ─────────────────────────────────────────

export interface PlannedJob {
  id?: string;
  clientId?: string;
  requirements?: {
    startAt?: string;
    endAt?: string;
    workerCount?: number;
    shift?: JobShift | null;
  };
}

/**
 * The units the order's plan produces (plan v1 "Hinnang ja proforma", B-1).
 * Each place's volume V = expectedHours / workerCount fills the shift
 * occurrences in order up to their net capacity (window − client pause), the
 * remainder on the last one. Each filled occurrence becomes a synthetic sheet
 * starting at the window start, its planned break placed at the same offset
 * the actual path places it — so a sheet that works the plan bills exactly
 * this. The result runs through the same unit function as real work.
 */
export function plannedBillingUnits(
  job: PlannedJob,
  pricing: JobPricingSnapshot,
  expectedHours: number = pricing.expectedHours ?? 0,
): BillingUnitResult[] {
  const sheets = plannedSheets(job, pricing, expectedHours);
  return sheets.length ? placeShiftBilling(sheets, { pricing }).units : [];
}

/**
 * The synthetic sheets of the order's plan (see `plannedBillingUnits`) — also
 * what the expected payroll cost classifies (M-D9: the worker's own
 * multipliers on the planned hour classes).
 */
export function plannedSheets(
  job: PlannedJob,
  pricing: JobPricingSnapshot,
  expectedHours: number = pricing.expectedHours ?? 0,
): Timesheet[] {
  const need = needOf(job as Parameters<typeof needOf>[0]);
  const occ = shiftOccurrences(need);
  const places = need.workerCount;
  const perPlace = Math.round((Math.max(0, expectedHours) * 60) / places);
  if (perPlace <= 0 || occ.length === 0) return [];
  const policy = pricing.clientTimeBilling;
  const b2c = pricing.segment === 'b2c';
  const planned = job.requirements?.shift?.breakMinutes;
  const breakMin = b2c || !policy?.deductBreaks ? 0 : (planned ?? policy.breakMinutes);
  const threshold = (policy?.thresholdHours ?? 6) * 60;
  // Net minutes a gross stretch g bills: the pause applies once work exceeds the threshold.
  const net = (g: number) => (breakMin > 0 && g > threshold ? g - Math.min(g, breakMin) : g);
  // The shortest gross stretch that nets n (the inverse of `net`).
  const grossFor = (n: number) => (breakMin > 0 && n > threshold ? n + breakMin : n);
  const sheets: Timesheet[] = [];
  for (let p = 1; p <= places; p += 1) {
    let left = perPlace;
    for (let i = 0; i < occ.length && left > 0; i += 1) {
      const o = occ[i];
      const window = Math.round((o.endMs - o.startMs) / MIN_MS);
      const capacity = net(window);
      const take = i === occ.length - 1 ? left : Math.min(capacity, left);
      left -= take;
      if (take <= 0) continue;
      const gross = grossFor(take);
      const id = `plan:p${p}:${o.date}`;
      sheets.push({
        id,
        jobId: job.id ?? 'plan',
        clientId: job.clientId ?? '',
        workerId: `plan:p${p}`,
        shiftId: id,
        status: 'approved',
        totalHours: take / 60,
        place: { placeId: `p${p}`, assignmentId: 'plan', shiftDate: o.date, source: 'segment_at_start' },
        shiftPlan: {
          startAt: o.startAt,
          endAt: o.endAt,
          ...(planned != null ? { breakMinutes: planned } : {}),
        },
        entries: [
          {
            checkInAt: new Date(o.startMs).toISOString(),
            checkOutAt: new Date(o.startMs + gross * MIN_MS).toISOString(),
            breakMinutes: 0,
          },
        ],
      } as unknown as Timesheet);
    }
  }
  return sheets;
}

/** Segments of a job for α (convenience for callers holding the Job). */
export function placeShiftContext(
  job: PlaceShiftJob | undefined,
  pricing: JobPricingSnapshot | undefined,
): PlaceShiftContext {
  return job
    ? { pricing, segments: segmentsOf(job), occurrences: shiftOccurrences(needOf(job)) }
    : { pricing };
}

// ── Row displays (every client-facing view) ──────────────────────────────────

/**
 * Per-sheet billed hours for one order, whatever its scope: the legacy rows
 * for a legacy order, the unit allocation for a place-and-shift order.
 */
export function billedRowsForJob(
  sheets: Timesheet[],
  pricing: JobPricingSnapshot | undefined,
  legacy: (sheets: Timesheet[], pricing?: JobPricingSnapshot) => Map<string, { hours: number; billedHours: number; breakMinutes: number }>,
  job?: PlaceShiftJob,
): Map<string, { hours: number; billedHours: number; breakMinutes: number; weightedBilledHours?: number }> {
  if (!isPlaceShiftPricing(pricing)) return legacy(sheets, pricing);
  const out = new Map<string, { hours: number; billedHours: number; breakMinutes: number; weightedBilledHours?: number }>();
  for (const [k, r] of placeShiftBilling(sheets, placeShiftContext(job, pricing)).rows)
    out.set(k, {
      hours: r.hours,
      billedHours: r.billedHours,
      breakMinutes: r.breakMinutes,
      ...(r.weightedBilledHours != null ? { weightedBilledHours: r.weightedBilledHours } : {}),
    });
  return out;
}

const CLIENT_HOUR_STATUSES = new Set(['submitted', 'approved', 'invoiced']);

/**
 * `clientBilledHoursBySheet` for every scope: a legacy order exactly as
 * before, a place-and-shift order by its units. `jobOf` gives the order (its
 * segments decide α); without it a sheet's minutes stay on its own place.
 */
export function clientBilledHoursBySheetFor(
  sheets: Timesheet[],
  pricingOf: (jobId: string) => JobPricingSnapshot | undefined,
  jobOf?: (jobId: string) => PlaceShiftJob | undefined,
): Map<string, { hours: number; billedHours: number; breakMinutes: number }> {
  const byJob = new Map<string, Timesheet[]>();
  for (const ts of sheets) {
    if (!CLIENT_HOUR_STATUSES.has(ts.status)) continue;
    byJob.set(ts.jobId, [...(byJob.get(ts.jobId) ?? []), ts]);
  }
  const out = new Map<string, { hours: number; billedHours: number; breakMinutes: number }>();
  for (const [jobId, list] of byJob)
    for (const [key, value] of billedRowsForJob(list, pricingOf(jobId), billableWorkRows, jobOf?.(jobId)))
      out.set(key, value);
  return out;
}
